/**
 * A generated test report, read by an operator.
 *
 * Not the student's view. The student gets prose; this shows the prose BESIDE
 * the ledger it was written over, because the question an operator has is
 * whether the model stated something the numbers do not support — and that is
 * unanswerable from either half alone.
 *
 * The body itself is rendered by `@shared-ui/test-report` — the teacher opens
 * the same report from a student's attempt, and a second copy of that renderer
 * would drift the moment the report shape changed. This page adds only what an
 * operator needs on top: the prompt it came from, and its citations.
 *
 * See docs/FeatureDesigns/student-performance-report-diagnosis/test-report-target.md.
 */

import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Badge, BackButton } from '@shared-ui/components';
import { adminApi } from '../../api/adminApi';
import { TestReportBody } from '@shared-ui/test-report';
import './AssignmentsAndTests.css';

export function TestReportViewer() {
  const { reportId } = useParams<{ reportId: string }>();
  const [params] = useSearchParams();
  const studentId = params.get('studentId') ?? '';
  const navigate = useNavigate();

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin-student-report', reportId, studentId],
    queryFn: () => adminApi.getStudentReport(reportId!, studentId),
    enabled: !!reportId && !!studentId,
  });

  const report = data?.report;

  if (!studentId) {
    return (
      <div className="aat-page">
        <BackButton onClick={() => navigate('/assignments-tests')} label="Assignments & Tests" />
        <div className="aat-error">This link is missing the student it belongs to. Open the report from the paper or the student.</div>
      </div>
    );
  }

  return (
    <div className="aat-page">
      <BackButton onClick={() => navigate(-1)} label="Back" />

      {isLoading && <div className="aat-sub">Loading…</div>}
      {error && <div className="aat-error">{error instanceof Error ? error.message : 'Could not load this report.'}</div>}

      {report && (
        <>
          <div className="aat-head">
            <h1 className="aat-h1">{report.title}</h1>
            <Badge variant={report.status === 'released' ? 'success' : 'default'} size="sm">{report.status}</Badge>
            <span className="aat-count">
              {report.createdAt ? format(new Date(report.createdAt), 'MMM d, yyyy HH:mm') : ''}
              {report.promptId && <> · prompt <span className="aat-mono">{report.promptId.slice(0, 8)}</span></>}
            </span>
          </div>

          {/* Above the report, not below it: the point is to be read BEFORE
              the prose is believed, and a note under a two-page document is a
              note nobody reaches. Worded as something to check rather than
              something that is wrong — most of these are the report quoting the
              student's own working back. */}
          {!!report.reviewFlags?.length && (
            <div className="trv-panel trv-review-panel">
              <h3 className="trv-h3">
                {report.reviewFlags.length} thing{report.reviewFlags.length === 1 ? '' : 's'} to check before releasing
              </h3>
              <p className="aat-sub">
                Figures below appear in the report but not in the computed ledger. Often that
                is the report quoting the student's own working; occasionally it is a number
                nobody computed.
              </p>
              <ul className="trv-review-list">
                {report.reviewFlags.map((f, i) => (
                  <li key={i}>
                    <span className="trv-review-where">
                      {f.kind === 'figure' ? `${f.field ?? 'report'} · ${f.value}` : 'structure'}
                    </span>
                    <span className="trv-review-detail">{f.detail}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <TestReportBody body={report.body} ledger={report.ledger} showLedger />

          {!!report.citations?.length && (
            <div className="trv-panel trv-cites-panel">
              <h3 className="trv-h3">Citations</h3>
              <ul className="trv-cites">
                {report.citations.map((c, i) => (
                  <li key={i}>
                    <div>{c.label}</div>
                    <div className="aat-sub aat-mono">{c.ref.kind} · {c.ref.id.slice(0, 10)}</div>
                  </li>
                ))}
              </ul>
            </div>
          )}

        </>
      )}
    </div>
  );
}
